import React from 'react';
import { useTranslation } from 'react-i18next';

import { EntryCard, EntryStatus } from '@/components/entry-card';
import type { EntryCardPayoutProps } from '@/components/entry-card';
import { Bet } from '@/feature/bets-sbk/hooks/types';
import { useSettledFooter } from '@/feature/bets-sbk/hooks/use-bet-card';
import { getAmountWonWithBonus, getNoSweatRefund } from '@/feature/bets-sbk/utils/bet-promo-utils';
import { classifyBetVariant } from '@/feature/bets-sbk/utils/classify-bet-variant';
import { useFormatCurrency } from '@/hooks/use-format-currency';

import { SbkEntryCardBottom } from '../SbkEntryCardBottom';
import { BONUS_ICON, useSbkCurrencyIcon, useSbkEntryStake } from './sbk-entry-card-currency';
import { useSbkEntryHeader } from './use-sbk-entry-header';
import { useSbkEntryPromos } from './use-sbk-entry-promos';
import { useSbkEntryStatus } from './use-sbk-entry-status';

/**
 * Payout block for a settled bet, keyed off the status badge:
 * - won / cashed out → the amount paid out
 * - lost → no payout ("--", dimmed, no currency icon); a rewarded No Sweat refund shows as
 *   "Bonus payout" in Betr Bucks instead
 * - refunded (push/cancel) → the stake returned
 */
const useSettledPayout = (bet: Bet, status: EntryStatus): EntryCardPayoutProps => {
    const { t } = useTranslation('bets');
    const { formatCurrency } = useFormatCurrency();
    const entry = useSbkEntryStake(bet);
    const currencyIcon = useSbkCurrencyIcon();

    switch (status) {
        case EntryStatus.Won:
        case EntryStatus.CashedOut:
        case EntryStatus.Sold:
            return {
                ...entry,
                payoutLabel: t('payout'),
                payoutAmount: formatCurrency(getAmountWonWithBonus(bet)),
                payoutIcon: currencyIcon,
            };
        case EntryStatus.Lost: {
            const refund = getNoSweatRefund(bet);
            if (refund != null) {
                return {
                    ...entry,
                    payoutLabel: t('bonus_payout'),
                    payoutAmount: formatCurrency(refund),
                    payoutIcon: BONUS_ICON,
                };
            }
            return { ...entry, payoutLabel: t('payout'), payoutAmount: '--', payoutColor: 'neutral300' };
        }
        // The refund returns what the bet actually cost, which is exactly the entry-side amount.
        case EntryStatus.Refunded:
            return {
                ...entry,
                payoutLabel: t('refunded'),
                payoutAmount: entry.entryAmount,
                payoutIcon: entry.entryIcon,
            };
        // A settled card is never Open; fall back to a dimmed, no-payout block.
        default:
            return { ...entry, payoutLabel: t('payout'), payoutAmount: '--', payoutColor: 'neutral300' };
    }
};

type Props = {
    bet: Bet;
    status: EntryStatus;
    onPress: () => void;
    onSharePress: () => void;
};

/** Settled (won / lost / refunded) sportsbook bet rendered on the shared entry card. */
export const SbkSettledEntryCard = ({ bet, status, onPress, onSharePress }: Props) => {
    const variant = classifyBetVariant(bet);
    const entryStatus = useSbkEntryStatus(status);
    const header = { ...useSbkEntryHeader(bet, variant), status: entryStatus };
    const payout = useSettledPayout(bet, entryStatus);
    const promos = useSbkEntryPromos(bet, true);
    const dateLine = useSettledFooter(bet);

    return (
        <EntryCard
            variant="sbk"
            header={header}
            payout={payout}
            promos={promos}
            bottom={<SbkEntryCardBottom dateLine={dateLine} entryId={bet.id} onSharePress={onSharePress} />}
            onPress={onPress}
        />
    );
};
